"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractWallet = void 0;
/* eslint-disable no-await-in-loop */
const circomlibjs_1 = require("circomlibjs");
const bn_js_1 = __importDefault(require("bn.js"));
const events_1 = __importDefault(require("events"));
const msgpack_lite_1 = __importDefault(require("msgpack-lite"));
const lodash_1 = __importDefault(require("lodash"));
const debugger_1 = __importDefault(require("../debugger/debugger"));
const bech32_1 = require("../key-derivation/bech32");
const wallet_node_1 = require("../key-derivation/wallet-node");
const event_types_1 = require("../models/event-types");
const formatted_types_1 = require("../models/formatted-types");
const memo_1 = require("../note/memo");
const bytes_1 = require("../utils/bytes");
const keys_utils_1 = require("../utils/keys-utils");
const wallet_types_1 = require("../models/wallet-types");
const babyjubjub_1 = require("../key-derivation/babyjubjub");
const chain_1 = require("../chain/chain");
const transact_note_1 = require("../note/transact-note");
const search_1 = require("../utils/search");
const keys_utils_legacy_1 = require("../utils/keys-utils-legacy");
const note_1 = require("../note");
const note_util_1 = require("../note/note-util");
const token_data_getter_1 = require("../token/token-data-getter");
const is_defined_1 = require("../utils/is-defined");
const commitment_1 = require("../utils/commitment");
const poi_1 = require("../poi/poi");
const blinded_commitment_1 = require("../poi/blinded-commitment");
const poi_types_1 = require("../models/poi-types");
const shield_railgun_txid_1 = require("../poi/shield-railgun-txid");
const merkletree_types_1 = require("../models/merkletree-types");
class AbstractWallet extends events_1.default {
    /**
     * Create Wallet controller
     * @param id - wallet ID
     * @param db - database
     */
    constructor(id, db, viewingKeyPair, spendingPublicKey, creationBlockNumbers) {
        super();
        this.utxoMerkletrees = [];
        this.railgunTxidMerkletrees = [];
        this.isRefreshingPOIs = [];
        // [type: [id: CachedStoredReceiveCommitment[]]]
        this.cachedReceiveCommitments = [];
        // [type: [id: CachedStoredSendCommitment[]]]
        this.cachedSendCommitments = [];
        this.id = (0, bytes_1.hexlify)(id);
        this.db = db;
        this.viewingKeyPair = viewingKeyPair;
        this.spendingPublicKey = spendingPublicKey;
        this.nullifyingKey = (0, circomlibjs_1.poseidon)([BigInt((0, bytes_1.hexlify)(this.viewingKeyPair.privateKey, true))]);
        this.masterPublicKey = wallet_node_1.WalletNode.getMasterPublicKey(spendingPublicKey, this.nullifyingKey);
        this.creationBlockNumbers = creationBlockNumbers;
    }
    /**
     * Loads utxo merkle tree into wallet
     */
    loadUTXOMerkletree(utxoMerkletree) {
        var _a, _b;
        (_a = this.utxoMerkletrees)[_b = utxoMerkletree.chain.type] ?? (_a[_b] = []);
        this.utxoMerkletrees[utxoMerkletree.chain.type][utxoMerkletree.chain.id] = utxoMerkletree;
    }
    /**
     * Loads txid merkle tree into wallet
     */
    loadRailgunTXIDMerkletree(railgunTxidMerkletree) {
        var _a, _b;
        (_a = this.railgunTxidMerkletrees)[_b = railgunTxidMerkletree.chain.type] ?? (_a[_b] = []);
        this.railgunTxidMerkletrees[railgunTxidMerkletree.chain.type][railgunTxidMerkletree.chain.id] =
            railgunTxidMerkletree;
    }
    /**
     * Unload utxo merkle tree by chain
     */
    unloadUTXOMerkletree(chain) {
        delete this.utxoMerkletrees[chain.type]?.[chain.id];
    }
    /**
     * Unload txid merkle tree by chain
     */
    unloadRailgunTXIDMerkletree(chain) {
        delete this.railgunTxidMerkletrees[chain.type]?.[chain.id];
    }
    createTokenDataGetter(chain) {
        return new token_data_getter_1.TokenDataGetter(this.db, chain);
    }
    /**
     * Construct DB TXO path from chain
     * Prefix consists of ['wallet', id, chain]
     * May be appended with tree and position
     * @param chain - chain type/id
     * @optional tree - without this param, all trees
     * @optional position - without this param, all positions
     * @returns wallet DB prefix
     */
    getWalletDBPrefix(chain, tree, position) {
        const path = [(0, bytes_1.fromUTF8String)('wallet'), (0, bytes_1.hexlify)(this.id), (0, chain_1.getChainFullNetworkID)(chain)].map((el) => (0, bytes_1.formatToByteLength)(el, bytes_1.ByteLength.UINT_256));
        if (tree != null) {
            path.push((0, bytes_1.hexlify)((0, bytes_1.padToLength)(new bn_js_1.default(tree), 32)));
        }
        if (position != null) {
            path.push((0, bytes_1.hexlify)((0, bytes_1.padToLength)(new bn_js_1.default(position), 32)));
        }
        return path;
    }
    /**
     * Construct DB received commitment path from chain
     * Prefix consists of ['wallet', id, chain]
     */
    getWalletReceiveCommitmentDBPrefix(chain, tree, position) {
        return this.getWalletDBPrefix(chain, tree, position);
    }
    /**
     * Construct DB spent commitment path from chain
     * Prefix consists of ['wallet', id + "-spent", chain]
     * May be appended with tree and position
     * @param chain - chain type/id
     * @optional tree - without this param, all trees
     * @optional position - without this param, all positions
     * @returns wallet DB prefix
     */
    getWalletSentCommitmentDBPrefix(chain, tree, position) {
        const path = [
            (0, bytes_1.fromUTF8String)('wallet'),
            `${(0, bytes_1.hexlify)(this.id)}-spent`,
            (0, chain_1.getChainFullNetworkID)(chain),
        ].map((el) => (0, bytes_1.formatToByteLength)(el, bytes_1.ByteLength.UINT_256));
        if (tree != null) {
            path.push((0, bytes_1.hexlify)((0, bytes_1.padToLength)(new bn_js_1.default(tree), 32)));
        }
        if (position != null) {
            path.push((0, bytes_1.hexlify)((0, bytes_1.padToLength)(new bn_js_1.default(position), 32)));
        }
        return path;
    }
    /**
     * Construct DB path from chain
     * @returns wallet DB path
     */
    getWalletDetailsPath(chain) {
        return this.getWalletDBPrefix(chain);
    }
    /**
     * Return object of Viewing privateKey and pubkey
     * @returns {ViewingKeyPair}
     */
    getViewingKeyPair() {
        return this.viewingKeyPair;
    }
    /**
     * Used only to sign Relayer fee messages.
     * Verified using Relayer's viewingPublicKey, which is encoded in its rail address.
     * @param {Uint8Array} message - message to sign as Uint8Array
     */
    async signWithViewingKey(message) {
        const viewingPrivateKey = this.getViewingKeyPair().privateKey;
        return (0, keys_utils_1.signED25519)(message, viewingPrivateKey);
    }
    /**
     * Nullifying Key (ie poseidon hash of Viewing Private Key) aka vpk derived on ed25519 curve
     * Used to decrypt and nullify notes
     * @returns {bigint}
     */
    getNullifyingKey() {
        return this.nullifyingKey;
    }
    /**
     * Get Viewing Public Key (VK)
     * @returns {Uint8Array}
     */
    get viewingPublicKey() {
        return this.viewingKeyPair.pubkey;
    }
    /**
     * Return masterPublicKey and viewingPublicKey used to encode RAILGUN addresses
     * @returns {AddressKeys}
     */
    get addressKeys() {
        return {
            masterPublicKey: this.masterPublicKey,
            viewingPublicKey: this.viewingPublicKey,
        };
    }
    /**
     * Encode address from (MPK, VK) + chain
     * @returns {string} bech32 encoded RAILGUN address
     */
    getAddress(chain) {
        return (0, bech32_1.encodeAddress)({ ...this.addressKeys, chain });
    }
    /**
     * Get encrypted wallet details for this wallet
     * @param chain - chain type/id
     * @returns walletDetails - including treeScannedHeight
     */
    async getWalletDetails(chain) {
        let walletDetails;
        try {
            // Try fetching from database
            const walletDetailsEncoded = (await this.db.get(this.getWalletDetailsPath(chain)));
            walletDetails = msgpack_lite_1.default.decode((0, bytes_1.arrayify)(walletDetailsEncoded));
            if (walletDetails.creationTreeHeight == null) {
                walletDetails.creationTreeHeight = undefined;
            }
        }
        catch {
            // If details don't exist yet, return defaults
            walletDetails = {
                treeScannedHeights: [],
                creationTree: undefined,
                creationTreeHeight: undefined,
            };
        }
        return walletDetails;
    }
    async decryptLeaf(ciphertext, memo, annotationData, sharedKey, blindedReceiverViewingKey, blindedSenderViewingKey, senderRandom, isSentNote, isLegacyDecryption, tokenDataGetter, blockNumber) {
        try {
            const decrypted = await transact_note_1.TransactNote.decrypt(this.addressKeys, ciphertext, sharedKey, memo, annotationData, blindedReceiverViewingKey, blindedSenderViewingKey, senderRandom, isSentNote, isLegacyDecryption, tokenDataGetter, blockNumber);
            return decrypted;
        }
        catch (err) {
            // Expect error if leaf not addressed to this wallet.
            return undefined;
        }
    }
    static getCommitmentType(commitment) {
        if ((0, is_defined_1.isDefined)(commitment.commitmentType)) {
            // New or legacy commitment.
            return commitment.commitmentType;
        }
        // Legacy commitments pre-v3 only.
        return 'ciphertext' in commitment
            ? formatted_types_1.CommitmentType.LegacyEncryptedCommitment
            : formatted_types_1.CommitmentType.LegacyGeneratedCommitment;
    }
    async createScannedDBCommitments(leaf, viewingPrivateKey, tree, chain, position, totalLeaves) {
        let noteReceive;
        let noteSend;
        let serializedNoteReceive;
        let serializedNoteSend;
        debugger_1.default.log(`Trying to decrypt commitment. Current index ${position}/${totalLeaves - 1}.`);
        const walletAddress = this.getAddress();
        const commitmentType = AbstractWallet.getCommitmentType(leaf);
        const tokenDataGetter = this.createTokenDataGetter(chain);
        switch (commitmentType) {
            case formatted_types_1.CommitmentType.TransactCommitment: {
                const commitment = leaf;
                const blindedSenderViewingKey = (0, bytes_1.hexStringToBytes)(commitment.ciphertext.blindedSenderViewingKey);
                const blindedReceiverViewingKey = (0, bytes_1.hexStringToBytes)(commitment.ciphertext.blindedReceiverViewingKey);
                const [sharedKeyReceiver, sharedKeySender] = await Promise.all([
                    (0, keys_utils_1.getSharedSymmetricKey)(viewingPrivateKey, blindedSenderViewingKey),
                    (0, keys_utils_1.getSharedSymmetricKey)(viewingPrivateKey, blindedReceiverViewingKey),
                ]);
                if (sharedKeyReceiver) {
                    noteReceive = await this.decryptLeaf(commitment.ciphertext.ciphertext, commitment.ciphertext.memo, commitment.ciphertext.annotationData, sharedKeyReceiver, blindedReceiverViewingKey, blindedSenderViewingKey, undefined, // senderRandom - not used
                    false, // isSentNote
                    false, // isLegacyDecryption
                    tokenDataGetter, leaf.blockNumber);
                    serializedNoteReceive = noteReceive ? noteReceive.serialize() : undefined;
                }
                if (sharedKeySender) {
                    const senderRandom = memo_1.Memo.decryptSenderRandom(commitment.ciphertext.annotationData, viewingPrivateKey);
                    noteSend = await this.decryptLeaf(commitment.ciphertext.ciphertext, commitment.ciphertext.memo, commitment.ciphertext.annotationData, sharedKeySender, blindedReceiverViewingKey, blindedSenderViewingKey, senderRandom, true, // isSentNote
                    false, // isLegacyDecryption
                    tokenDataGetter, leaf.blockNumber);
                    serializedNoteSend = noteSend ? noteSend.serialize() : undefined;
                }
                break;
            }
            case formatted_types_1.CommitmentType.ShieldCommitment: {
                const commitment = leaf;
                const sharedKey = await (0, keys_utils_1.getSharedSymmetricKey)(viewingPrivateKey, (0, bytes_1.hexToBytes)(commitment.shieldKey));
                try {
                    if (!sharedKey) {
                        throw new Error('No sharedKey from shield note');
                    }
                    const random = note_1.ShieldNote.decryptRandom(commitment.encryptedBundle, sharedKey);
                    const serialized = {
                        npk: commitment.preImage.npk,
                        random,
                        token: (0, note_util_1.getTokenDataHash)(commitment.preImage.token),
                        value: commitment.preImage.value,
                        annotationData: '',
                        recipientAddress: walletAddress,
                        outputType: formatted_types_1.OutputType.Transfer,
                        senderAddress: undefined,
                        memoText: undefined,
                        shieldFee: commitment.fee,
                        blockNumber: leaf.blockNumber,
                    };
                    noteReceive = await transact_note_1.TransactNote.deserialize(serialized, viewingPrivateKey, tokenDataGetter);
                    serializedNoteReceive = serialized;
                }
                catch (err) {
                    // Expect error if leaf not addressed to this wallet.
                }
                break;
            }
            case formatted_types_1.CommitmentType.LegacyEncryptedCommitment: {
                const commitment = leaf;
                const blindedSenderViewingKey = (0, bytes_1.hexStringToBytes)(commitment.ciphertext.ephemeralKeys[0]);
                const blindedReceiverViewingKey = (0, bytes_1.hexStringToBytes)(commitment.ciphertext.ephemeralKeys[1]);
                const [sharedKeyReceiver, sharedKeySender] = await Promise.all([
                    (0, keys_utils_legacy_1.getSharedSymmetricKeyLegacy)(viewingPrivateKey, blindedSenderViewingKey),
                    (0, keys_utils_legacy_1.getSharedSymmetricKeyLegacy)(viewingPrivateKey, blindedReceiverViewingKey),
                ]);
                const annotationData = (0, bytes_1.combine)(commitment.ciphertext.memo.slice(0, memo_1.LEGACY_MEMO_METADATA_BYTE_CHUNKS));
                const memo = (0, bytes_1.combine)(commitment.ciphertext.memo.slice(memo_1.LEGACY_MEMO_METADATA_BYTE_CHUNKS));
                if (sharedKeyReceiver) {
                    noteReceive = await this.decryptLeaf(commitment.ciphertext.ciphertext, memo, annotationData, sharedKeyReceiver, blindedReceiverViewingKey, blindedSenderViewingKey, undefined, // senderRandom - not used
                    false, // isSentNote
                    true, // isLegacyDecryption
                    tokenDataGetter, leaf.blockNumber);
                    serializedNoteReceive = noteReceive
                        ? noteReceive.serializeLegacy(viewingPrivateKey)
                        : undefined;
                }
                if (sharedKeySender) {
                    const senderRandom = memo_1.Memo.decryptSenderRandom(annotationData, viewingPrivateKey);
                    noteSend = await this.decryptLeaf(commitment.ciphertext.ciphertext, memo, annotationData, sharedKeySender, blindedReceiverViewingKey, blindedSenderViewingKey, senderRandom, true, // isSentNote
                    true, // isLegacyDecryption
                    tokenDataGetter, leaf.blockNumber);
                    serializedNoteSend = noteSend ? noteSend.serializeLegacy(viewingPrivateKey) : undefined;
                }
                break;
            }
            case formatted_types_1.CommitmentType.LegacyGeneratedCommitment: {
                const commitment = leaf;
                const serialized = {
                    npk: commitment.preImage.npk,
                    encryptedRandom: commitment.encryptedRandom,
                    token: commitment.preImage.token.tokenAddress,
                    value: commitment.preImage.value,
                    recipientAddress: walletAddress,
                    memoField: [],
                    memoText: undefined,
                    blockNumber: leaf.blockNumber,
                };
                try {
                    noteReceive = await transact_note_1.TransactNote.deserialize(serialized, viewingPrivateKey, tokenDataGetter);
                    serializedNoteReceive = serialized;
                }
                catch (err) {
                    // Expect error if leaf not addressed to us.
                }
                break;
            }
        }
        const scannedCommitments = [];
        if ((noteReceive && !serializedNoteReceive) || (noteSend && !serializedNoteSend)) {
            throw new Error('Scan requires a serialized note.');
        }
        if (noteReceive && serializedNoteReceive) {
            const nullifier = transact_note_1.TransactNote.getNullifier(this.nullifyingKey, position);
            const storedReceiveCommitment = {
                spendtxid: false,
                txid: leaf.txid,
                timestamp: leaf.timestamp,
                nullifier: (0, bytes_1.nToHex)(nullifier, bytes_1.ByteLength.UINT_256),
                decrypted: noteReceive.serialize(),
                senderAddress: noteReceive.senderAddressData
                    ? (0, bech32_1.encodeAddress)(noteReceive.senderAddressData)
                    : undefined,
                commitmentType: leaf.commitmentType,
                railgunTxid: (0, commitment_1.isSentCommitment)(leaf) ? leaf.railgunTxid : undefined,
                poisPerList: undefined,
                blindedCommitment: undefined,
            };
            debugger_1.default.log(`Adding RECEIVE commitment at ${position} (Wallet ${this.id}).`);
            scannedCommitments.push({
                type: 'put',
                key: this.getWalletReceiveCommitmentDBPrefix(chain, tree, position).join(':'),
                value: msgpack_lite_1.default.encode(storedReceiveCommitment),
            });
            // AbstractWallet.addCachedStoredCommitment(chain, this.cachedReceiveCommitments, {
            //   storedReceiveCommitment,
            //   tree,
            //   position,
            // });
        }
        if (noteSend && serializedNoteSend) {
            const storedSendCommitment = {
                txid: leaf.txid,
                timestamp: leaf.timestamp,
                decrypted: serializedNoteSend,
                commitmentType: leaf.commitmentType,
                noteExtraData: memo_1.Memo.decryptNoteAnnotationData(noteSend.annotationData, viewingPrivateKey),
                recipientAddress: (0, bech32_1.encodeAddress)(noteSend.receiverAddressData),
                railgunTxid: undefined,
                poisPerList: undefined,
                blindedCommitment: undefined,
            };
            debugger_1.default.log(`Adding SPEND commitment at ${position} (Wallet ${this.id}).`);
            scannedCommitments.push({
                type: 'put',
                key: this.getWalletSentCommitmentDBPrefix(chain, tree, position).join(':'),
                value: msgpack_lite_1.default.encode(storedSendCommitment),
            });
            // AbstractWallet.addCachedStoredCommitment(chain, this.cachedSendCommitments, {
            //   storedSendCommitment,
            //   tree,
            //   position,
            // });
        }
        return scannedCommitments;
    }
    /**
     * Scans wallet at index for new balances
     * Commitment index in array should be same as commitment index in tree
     * @param {Commitment[]} leaves - commitments from events to attempt parsing
     * @param {number} tree - tree number we're scanning
     * @param {number} chain - chain type/id we're scanning
     * @param {number} startScanHeight - starting position
     */
    async scanLeaves(leaves, tree, chain, startScanHeight, treeHeight, scanTicker) {
        debugger_1.default.log(`wallet:scanLeaves tree:${tree} chain:${chain.type}:${chain.id} leaves:${leaves.length}, startScanHeight:${startScanHeight}`);
        const vpk = this.getViewingKeyPair().privateKey;
        const leafSyncPromises = [];
        for (let position = startScanHeight; position < treeHeight; position += 1) {
            const leaf = leaves[position];
            if (leaf == null) {
                scanTicker();
                continue;
            }
            const scanPromiseWithTicker = async () => {
                const scanned = await this.createScannedDBCommitments(leaf, vpk, tree, chain, position, leaves.length);
                scanTicker();
                return scanned;
            };
            leafSyncPromises.push(scanPromiseWithTicker());
        }
        const writeBatch = (await Promise.all(leafSyncPromises)).flat();
        // Write to DB
        await this.db.batch(writeBatch);
    }
    async keySplits(namespace, fieldCount) {
        const keys = await this.db.getNamespaceKeys(namespace);
        const keySplits = keys
            .map((key) => key.split(':'))
            .filter((keySplit) => keySplit.length === fieldCount);
        return keySplits;
    }
    async queryAllStoredReceiveCommitments(chain) {
        const namespace = this.getWalletDBPrefix(chain);
        const keySplits = await this.keySplits(namespace, 5);
        const dbStoredReceiveCommitments = await Promise.all(keySplits.map(async (keySplit) => {
            const data = (await this.db.get(keySplit));
            const storedReceiveCommitment = msgpack_lite_1.default.decode((0, bytes_1.arrayify)(data));
            const tree = (0, bytes_1.numberify)(keySplit[3]).toNumber();
            const position = (0, bytes_1.numberify)(keySplit[4]).toNumber();
            return { storedReceiveCommitment, tree, position };
        }));
        return dbStoredReceiveCommitments;
    }
    async queryAllStoredSendCommitments(chain) {
        // if (
        //   isDefined(this.cachedSendCommitments[chain.type]) &&
        //   isDefined(this.cachedSendCommitments[chain.type][chain.id])
        // ) {
        //   return this.cachedSendCommitments[chain.type][chain.id];
        // }
        const namespace = this.getWalletSentCommitmentDBPrefix(chain);
        const keySplits = await this.keySplits(namespace, 5);
        const dbStoredSendCommitments = await Promise.all(keySplits.map(async (keySplit) => {
            const data = (await this.db.get(keySplit));
            const storedSendCommitment = msgpack_lite_1.default.decode((0, bytes_1.arrayify)(data));
            const tree = (0, bytes_1.numberify)(keySplit[3]).toNumber();
            const position = (0, bytes_1.numberify)(keySplit[4]).toNumber();
            return { storedSendCommitment, tree, position };
        }));
        // this.cachedSendCommitments[chain.type] ??= [];
        // this.cachedSendCommitments[chain.type][chain.id] = dbStoredSendCommitments;
        return dbStoredSendCommitments;
    }
    // private static addCachedStoredCommitment<Cache extends { tree: number; position: number }[][][]>(
    //   chain: Chain,
    //   commitmentCache: Cache,
    //   cachedCommitment: CachedStoredReceiveCommitment | CachedStoredSendCommitment,
    // ) {
    //   if (
    //     !isDefined(commitmentCache[chain.type]) ||
    //     !isDefined(commitmentCache[chain.type][chain.id])
    //   ) {
    //     return;
    //   }
    //   const cacheForChain = commitmentCache[chain.type][chain.id];
    //   const found = cacheForChain.find((stored) => {
    //     return stored.tree === cachedCommitment.tree && stored.position === cachedCommitment.position;
    //   });
    //   if (found) {
    //     return;
    //   }
    //   cacheForChain.push(cachedCommitment);
    // }
    /**
     * Get TXOs list of a chain
     * @param chain - chain type/id to get UTXOs for
     * @returns UTXOs list
     */
    async TXOs(chain) {
        const recipientAddress = (0, bech32_1.encodeAddress)(this.addressKeys);
        const vpk = this.getViewingKeyPair().privateKey;
        const merkletree = this.getUTXOMerkletreeForChain(chain);
        const tokenDataGetter = this.createTokenDataGetter(chain);
        const storedReceiveCommitments = await this.queryAllStoredReceiveCommitments(chain);
        return Promise.all(storedReceiveCommitments.map(async ({ storedReceiveCommitment, tree, position }) => {
            const receiveCommitment = storedReceiveCommitment;
            const note = await transact_note_1.TransactNote.deserialize({
                ...receiveCommitment.decrypted,
                recipientAddress,
            }, vpk, tokenDataGetter);
            // Check if TXO has been spent.
            if (receiveCommitment.spendtxid === false) {
                const nullifierTxid = await merkletree.getNullifierTxid(receiveCommitment.nullifier);
                if ((0, is_defined_1.isDefined)(nullifierTxid)) {
                    receiveCommitment.spendtxid = nullifierTxid;
                    await this.updateReceiveCommitmentInDB(chain, tree, position, receiveCommitment);
                }
            }
            // Look up creation railgunTxid in commitments.
            if (!(0, is_defined_1.isDefined)(receiveCommitment.railgunTxid) ||
                !(0, is_defined_1.isDefined)(receiveCommitment.blindedCommitment)) {
                const isShield = (0, commitment_1.isReceiveShieldCommitment)(receiveCommitment);
                if (isShield) {
                    const railgunTxid = (0, shield_railgun_txid_1.getShieldRailgunTxid)(tree, position);
                    receiveCommitment.railgunTxid = railgunTxid;
                    const commitment = await merkletree.getCommitment(tree, position);
                    receiveCommitment.blindedCommitment = (0, blinded_commitment_1.getBlindedCommitmentForShield)(commitment.hash, note.notePublicKey, railgunTxid);
                }
                else {
                    const commitment = await merkletree.getCommitment(tree, position);
                    if ((0, commitment_1.isSentCommitment)(commitment) && (0, is_defined_1.isDefined)(commitment.railgunTxid)) {
                        receiveCommitment.railgunTxid = commitment.railgunTxid;
                        receiveCommitment.blindedCommitment = (0, blinded_commitment_1.getBlindedCommitmentForTransact)(commitment, note.notePublicKey, commitment.railgunTxid);
                    }
                }
                await this.updateReceiveCommitmentInDB(chain, tree, position, receiveCommitment);
            }
            const txo = {
                tree,
                position,
                txid: receiveCommitment.txid,
                timestamp: receiveCommitment.timestamp,
                spendtxid: receiveCommitment.spendtxid,
                nullifier: receiveCommitment.nullifier,
                note,
                railgunTxid: receiveCommitment.railgunTxid,
                poisPerList: receiveCommitment.poisPerList,
                blindedCommitment: receiveCommitment.blindedCommitment,
                commitmentType: receiveCommitment.commitmentType,
            };
            return txo;
        }));
    }
    /**
     * Get spent commitments of a chain
     * @param chain - chain type/id to get spent commitments for
     * @returns SentCommitment list
     */
    async getSentCommitments(chain, startingBlock) {
        const vpk = this.getViewingKeyPair().privateKey;
        const tokenDataGetter = this.createTokenDataGetter(chain);
        const sentCommitments = [];
        const storedSendCommitments = await this.queryAllStoredSendCommitments(chain);
        await Promise.all(storedSendCommitments.map(async ({ storedSendCommitment, tree, position }) => {
            const sentCommitment = storedSendCommitment;
            const note = await transact_note_1.TransactNote.deserialize(sentCommitment.decrypted, vpk, tokenDataGetter);
            if (!(0, is_defined_1.isDefined)(note.blockNumber)) {
                return;
            }
            if (startingBlock != null && note.blockNumber < startingBlock) {
                return;
            }
            // Look up railgunTxid.
            if (!(0, is_defined_1.isDefined)(sentCommitment.railgunTxid) ||
                !(0, is_defined_1.isDefined)(sentCommitment.blindedCommitment)) {
                const utxoMerkletree = this.getUTXOMerkletreeForChain(chain);
                const commitment = await utxoMerkletree.getCommitment(tree, position);
                if ((0, commitment_1.isSentCommitment)(commitment) && (0, is_defined_1.isDefined)(commitment.railgunTxid)) {
                    sentCommitment.blindedCommitment = (0, blinded_commitment_1.getBlindedCommitmentForTransact)(commitment, note.notePublicKey, commitment.railgunTxid);
                    sentCommitment.railgunTxid = commitment.railgunTxid;
                }
                await this.updateSentCommitmentInDB(chain, tree, position, sentCommitment);
            }
            sentCommitments.push({
                tree,
                position,
                txid: sentCommitment.txid,
                timestamp: sentCommitment.timestamp,
                note,
                noteAnnotationData: sentCommitment.noteExtraData,
                isLegacyTransactNote: transact_note_1.TransactNote.isLegacyTransactNote(sentCommitment.decrypted),
                railgunTxid: sentCommitment.railgunTxid,
                poisPerList: sentCommitment.poisPerList,
                blindedCommitment: sentCommitment.blindedCommitment,
                commitmentType: sentCommitment.commitmentType,
            });
        }));
        return sentCommitments;
    }
    async updateReceiveCommitmentInDB(chain, tree, position, receiveCommitment) {
        await this.db.put(this.getWalletReceiveCommitmentDBPrefix(chain, tree, position), msgpack_lite_1.default.encode(receiveCommitment));
        // Update cache
        // this.cachedReceiveCommitments?.[chain.type]?.[chain.id].forEach((cachedCommitment) => {
        //   if (cachedCommitment.tree === tree && cachedCommitment.position === position) {
        //     // eslint-disable-next-line no-param-reassign
        //     cachedCommitment.storedReceiveCommitment = receiveCommitment;
        //   }
        // });
    }
    async updateSentCommitmentInDB(chain, tree, position, sentCommitment) {
        await this.db.put(this.getWalletSentCommitmentDBPrefix(chain, tree, position), msgpack_lite_1.default.encode(sentCommitment));
        // Update cache
        // this.cachedSendCommitments?.[chain.type]?.[chain.id].forEach((cachedCommitment) => {
        //   if (cachedCommitment.tree === tree && cachedCommitment.position === position) {
        //     // eslint-disable-next-line no-param-reassign
        //     cachedCommitment.storedSendCommitment = sentCommitment;
        //   }
        // });
    }
    async getTXOsReceivedPOIStatusInfo(chain) {
        const TXOs = await this.TXOs(chain);
        const txidGroups = lodash_1.default.groupBy(TXOs, (txo) => txo.txid);
        const statusInfos = [];
        await Promise.all(Object.keys(txidGroups).map(async (txid) => {
            const txosForTxid = txidGroups[txid];
            const railgunTxidGroups = lodash_1.default.groupBy(txosForTxid, (txo) => txo.railgunTxid ?? 'Missing');
            await Promise.all(Object.keys(railgunTxidGroups).map(async (railgunTxid) => {
                const txosForTxidAndRailgunTxid = railgunTxidGroups[railgunTxid];
                const firstTxo = txosForTxidAndRailgunTxid[0];
                const { tree } = firstTxo;
                const startPosition = txosForTxidAndRailgunTxid.map((txo) => txo.position).sort()[0];
                const statusInfo = {
                    tree,
                    startPosition,
                    txid,
                    txos: `${txosForTxidAndRailgunTxid.length}: com ${txosForTxidAndRailgunTxid
                        .map((txo) => `${(0, bytes_1.nToHex)(txo.note.hash, bytes_1.ByteLength.UINT_256, true)} (${txo.commitmentType})`)
                        .join(', ')}`,
                    railgunTxid,
                    blindedCommitments: `${txosForTxidAndRailgunTxid
                        .map((txo) => txo.blindedCommitment ?? 'Unavailable')
                        .join(', ')}`,
                    poiStatuses: txosForTxidAndRailgunTxid.map((txo) => txo.poisPerList),
                };
                return statusInfos.push(statusInfo);
            }));
        }));
        // Sort descending by UTXO tree/position.
        return statusInfos.sort((a, b) => b.tree * merkletree_types_1.TREE_MAX_ITEMS + b.startPosition - (a.tree * merkletree_types_1.TREE_MAX_ITEMS + a.startPosition));
    }
    async getTXOsSpentPOIStatusInfo(chain) {
        const [sentCommitments, TXOs] = await Promise.all([
            this.getSentCommitments(chain, undefined),
            this.TXOs(chain),
        ]);
        const unshieldEvents = await this.getAllUnshieldEventsFromSpentNullifiers(chain, TXOs);
        const txidGroups = {};
        sentCommitments.forEach((sentCommitment) => {
            var _a;
            txidGroups[_a = sentCommitment.txid] ?? (txidGroups[_a] = {
                sentCommitments: [],
                unshieldEvents: [],
            });
            txidGroups[sentCommitment.txid].sentCommitments.push(sentCommitment);
        });
        unshieldEvents.forEach((unshieldEvent) => {
            var _a;
            txidGroups[_a = unshieldEvent.txid] ?? (txidGroups[_a] = {
                sentCommitments: [],
                unshieldEvents: [],
            });
            txidGroups[unshieldEvent.txid].unshieldEvents.push(unshieldEvent);
        });
        const txidMerkletree = this.getRailgunTXIDMerkletreeForChain(chain);
        const statusInfos = [];
        await Promise.all(Object.keys(txidGroups).map(async (txid) => {
            const txidGroup = txidGroups[txid];
            const railgunTxidGroups = {};
            txidGroup.sentCommitments.forEach((sentCommitment) => {
                var _a;
                if (!(0, is_defined_1.isDefined)(sentCommitment.railgunTxid)) {
                    return;
                }
                railgunTxidGroups[_a = sentCommitment.railgunTxid] ?? (railgunTxidGroups[_a] = {
                    sentCommitments: [],
                    unshieldEvents: [],
                });
                railgunTxidGroups[sentCommitment.railgunTxid].sentCommitments.push(sentCommitment);
            });
            txidGroup.unshieldEvents.forEach((unshieldEvent) => {
                var _a;
                if (!(0, is_defined_1.isDefined)(unshieldEvent.railgunTxid)) {
                    return;
                }
                railgunTxidGroups[_a = unshieldEvent.railgunTxid] ?? (railgunTxidGroups[_a] = {
                    sentCommitments: [],
                    unshieldEvents: [],
                });
                railgunTxidGroups[unshieldEvent.railgunTxid].unshieldEvents.push(unshieldEvent);
            });
            await Promise.all(Object.keys(railgunTxidGroups).map(async (railgunTxid) => {
                const sentCommitmentsForRailgunTxid = railgunTxidGroups[railgunTxid].sentCommitments;
                const unshieldEventsForRailgunTxid = railgunTxidGroups[railgunTxid].unshieldEvents;
                const blockNumber = sentCommitmentsForRailgunTxid.length
                    ? sentCommitmentsForRailgunTxid[0].note.blockNumber
                    : unshieldEventsForRailgunTxid[0].blockNumber;
                const commitmentHashes = [
                    ...sentCommitmentsForRailgunTxid.map((sentCommitment) => (0, bytes_1.nToHex)(sentCommitment.note.hash, bytes_1.ByteLength.UINT_256, true)),
                    ...unshieldEventsForRailgunTxid.map((unshieldEvent) => (0, bytes_1.nToHex)((0, note_util_1.getUnshieldEventNoteHash)(unshieldEvent), bytes_1.ByteLength.UINT_256, true)),
                ];
                let railgunTransactionInfo;
                if (railgunTxid && railgunTxid !== 'Missing') {
                    const railgunTransaction = await txidMerkletree.getRailgunTransactionByTxid(railgunTxid);
                    if (railgunTransaction) {
                        const nul = railgunTransaction.nullifiers;
                        const hasAllNul = nul.every((n) => TXOs.some((txo) => `0x${txo.nullifier}` === n));
                        const com = railgunTransaction.commitments;
                        const hasAllCom = com.every((c) => commitmentHashes.includes(c));
                        railgunTransactionInfo = `${nul.length} nul: ${nul.join(', ')} (${hasAllNul ? '✓' : 'x'}), ${com.length} com: ${com.join(', ')} (${hasAllCom ? '✓' : 'x'})`;
                    }
                    else {
                        railgunTransactionInfo = 'Not found';
                    }
                }
                else {
                    railgunTransactionInfo = 'Missing';
                }
                const statusInfo = {
                    blockNumber: blockNumber ?? 0,
                    txid,
                    railgunTxid,
                    railgunTransactionInfo,
                    sentCommitmentsBlinded: `${sentCommitmentsForRailgunTxid
                        .map((sentCommitment) => sentCommitment.blindedCommitment ?? 'Unavailable')
                        .join(', ')}`,
                    poiStatusesSentCommitments: sentCommitmentsForRailgunTxid.map((sentCommitment) => sentCommitment.poisPerList),
                    unshieldEventsBlinded: `${unshieldEventsForRailgunTxid
                        .map((unshieldEvent) => unshieldEvent.blindedCommitment ?? 'Unavailable')
                        .join(', ')}`,
                    poiStatusesUnshieldEvents: unshieldEventsForRailgunTxid.map((unshieldEvent) => unshieldEvent.poisPerList),
                };
                return statusInfos.push(statusInfo);
            }));
        }));
        // Sort descending by blockNumber.
        return statusInfos.sort((a, b) => b.blockNumber - a.blockNumber);
    }
    async refreshReceivePOIsAllTXOs(chain, filterRailgunTxid) {
        const TXOs = await this.TXOs(chain);
        const txosNeedCreationPOIs = TXOs.filter((txo) => poi_1.POI.shouldRetrieveCreationPOIs(txo))
            .filter((txo) => AbstractWallet.filterByRailgunTxid(txo, filterRailgunTxid))
            .sort((a, b) => AbstractWallet.sortPOIsPerListUndefinedFirst(a, b));
        if (txosNeedCreationPOIs.length === 0) {
            return;
        }
        const blindedCommitmentDatas = txosNeedCreationPOIs.map((txo) => ({
            type: (0, commitment_1.isSentCommitmentType)(txo.commitmentType)
                ? poi_types_1.BlindedCommitmentType.Transact
                : poi_types_1.BlindedCommitmentType.Shield,
            blindedCommitment: txo.blindedCommitment,
        }));
        const blindedCommitmentToPOIList = await poi_1.POI.retrievePOIsForBlindedCommitments(chain, blindedCommitmentDatas);
        // eslint-disable-next-line no-restricted-syntax
        for (const txo of txosNeedCreationPOIs) {
            if (!(0, is_defined_1.isDefined)(txo.blindedCommitment)) {
                continue;
            }
            const poisPerList = blindedCommitmentToPOIList[txo.blindedCommitment];
            if (!(0, is_defined_1.isDefined)(poisPerList)) {
                continue;
            }
            txo.poisPerList = poisPerList;
            await this.updateReceiveCommitmentCreatedPOIs(chain, txo.tree, txo.position, txo.poisPerList);
        }
    }
    static sortPOIsPerListUndefinedFirst(a, b) {
        return (0, is_defined_1.isDefined)(a.poisPerList) && !(0, is_defined_1.isDefined)(b.poisPerList) ? -1 : 1;
    }
    static filterByRailgunTxid(item, railgunTxid) {
        return (0, is_defined_1.isDefined)(railgunTxid) ? item.railgunTxid === railgunTxid : true;
    }
    async getSentCommitmentsAndUnshieldEventsNeedPOIs(chain, filterRailgunTxid) {
        const [sentCommitments, TXOs] = await Promise.all([
            this.getSentCommitments(chain, undefined),
            this.TXOs(chain),
        ]);
        const sentCommitmentsNeedPOIs = sentCommitments
            .filter((sendCommitment) => poi_1.POI.shouldRetrieveSpentPOIs(sendCommitment))
            .filter((sentCommitment) => AbstractWallet.filterByRailgunTxid(sentCommitment, filterRailgunTxid))
            .sort((a, b) => AbstractWallet.sortPOIsPerListUndefinedFirst(a, b));
        const unshieldEvents = await this.getAllUnshieldEventsFromSpentNullifiers(chain, TXOs);
        const unshieldEventsNeedPOIs = unshieldEvents
            .filter((unshieldEvent) => poi_1.POI.shouldGenerateSpentPOIsUnshieldEvent(unshieldEvent))
            .filter((unshieldEvent) => AbstractWallet.filterByRailgunTxid(unshieldEvent, filterRailgunTxid))
            .sort((a, b) => AbstractWallet.sortPOIsPerListUndefinedFirst(a, b));
        return { sentCommitmentsNeedPOIs, unshieldEventsNeedPOIs, TXOs };
    }
    async refreshSpentPOIsAllSentCommitmentsAndUnshieldEvents(chain, filterRailgunTxid) {
        const { sentCommitmentsNeedPOIs, unshieldEventsNeedPOIs } = await this.getSentCommitmentsAndUnshieldEventsNeedPOIs(chain, filterRailgunTxid);
        if (!sentCommitmentsNeedPOIs.length && !unshieldEventsNeedPOIs.length) {
            return;
        }
        const blindedCommitmentDatas = [
            ...sentCommitmentsNeedPOIs.map((sentCommitment) => ({
                type: (0, commitment_1.isSentCommitmentType)(sentCommitment.commitmentType)
                    ? poi_types_1.BlindedCommitmentType.Transact
                    : poi_types_1.BlindedCommitmentType.Shield,
                blindedCommitment: sentCommitment.blindedCommitment,
            })),
            ...unshieldEventsNeedPOIs.map((unshieldEvent) => ({
                type: poi_types_1.BlindedCommitmentType.Transact,
                blindedCommitment: unshieldEvent.blindedCommitment,
            })),
        ];
        const blindedCommitmentToPOIList = await poi_1.POI.retrievePOIsForBlindedCommitments(chain, blindedCommitmentDatas);
        // eslint-disable-next-line no-restricted-syntax
        for (const sentCommitment of sentCommitmentsNeedPOIs) {
            if (!(0, is_defined_1.isDefined)(sentCommitment.blindedCommitment)) {
                continue;
            }
            const poisPerList = blindedCommitmentToPOIList[sentCommitment.blindedCommitment];
            if (!(0, is_defined_1.isDefined)(poisPerList)) {
                continue;
            }
            sentCommitment.poisPerList = poisPerList;
            await this.updateSentCommitmentSpentPOIs(chain, sentCommitment.tree, sentCommitment.position, sentCommitment.poisPerList);
        }
        const utxoMerkletree = this.getUTXOMerkletreeForChain(chain);
        // eslint-disable-next-line no-restricted-syntax
        for (const unshieldEvent of unshieldEventsNeedPOIs) {
            if (!(0, is_defined_1.isDefined)(unshieldEvent.blindedCommitment)) {
                continue;
            }
            const poisPerList = blindedCommitmentToPOIList[unshieldEvent.blindedCommitment];
            if (!(0, is_defined_1.isDefined)(poisPerList)) {
                continue;
            }
            unshieldEvent.poisPerList = poisPerList;
            await utxoMerkletree.updateUnshieldEvent(unshieldEvent);
        }
    }
    async generatePOIsAllSentCommitmentsAndUnshieldEvents(chain, railgunTxidFilter) {
        const { sentCommitmentsNeedPOIs, unshieldEventsNeedPOIs, TXOs } = await this.getSentCommitmentsAndUnshieldEventsNeedPOIs(chain, railgunTxidFilter);
        if (!sentCommitmentsNeedPOIs.length && !unshieldEventsNeedPOIs.length) {
            return;
        }
        const railgunTxidsNeedPOIs = new Set();
        sentCommitmentsNeedPOIs.forEach((sentCommitment) => {
            if ((0, is_defined_1.isDefined)(sentCommitment.railgunTxid)) {
                railgunTxidsNeedPOIs.add(sentCommitment.railgunTxid);
            }
        });
        unshieldEventsNeedPOIs.forEach((unshieldEvent) => {
            if ((0, is_defined_1.isDefined)(unshieldEvent.railgunTxid)) {
                railgunTxidsNeedPOIs.add(unshieldEvent.railgunTxid);
            }
        });
        const railgunTxids = Array.from(railgunTxidsNeedPOIs);
        if ((0, is_defined_1.isDefined)(railgunTxidFilter) && !railgunTxids.includes(railgunTxidFilter)) {
            throw new Error('Railgun TXID not found in sent commitments / unshield events.');
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const railgunTxid of railgunTxids) {
            await this.generatePOIsForRailgunTxid(chain, TXOs, railgunTxid, sentCommitmentsNeedPOIs, unshieldEventsNeedPOIs);
        }
    }
    async generatePOIsForRailgunTxid(chain, TXOs, railgunTxid, sentCommitments, unshieldEvents) {
        try {
            const railgunTxidMerkletree = this.getRailgunTXIDMerkletreeForChain(chain);
            const railgunTxidMerkletreeData = await railgunTxidMerkletree.getRailgunTxidCurrentMerkletreeData(railgunTxid);
            const { railgunTransaction } = railgunTxidMerkletreeData;
            // Spent TXOs
            const spentTXOs = TXOs.filter((txo) => railgunTransaction.nullifiers.includes(`0x${txo.nullifier}`));
            if (!spentTXOs.length) {
                throw new Error('No spent txos for railgun txid nullifier');
            }
            const blindedCommitmentsIn = (0, is_defined_1.removeUndefineds)(spentTXOs.map((txo) => txo.blindedCommitment));
            if (blindedCommitmentsIn.length !== railgunTransaction.nullifiers.length) {
                throw new Error(`Not enough TXO blinded commitments for railgun transaction nullifiers: expected ${railgunTransaction.nullifiers.length}, got ${blindedCommitmentsIn.length}`);
            }
            const creationTxidsIn = AbstractWallet.getCreationTxidsIn(spentTXOs);
            // Sent Commitments
            const sentCommitmentsForRailgunTxid = sentCommitments.filter((sentCommitment) => sentCommitment.railgunTxid === railgunTransaction.hash);
            const blindedCommitmentsOutSentCommitments = (0, is_defined_1.removeUndefineds)(sentCommitmentsForRailgunTxid.map((sentCommitment) => sentCommitment.blindedCommitment));
            // Unshield Events
            const unshieldEventsForRailgunTxid = unshieldEvents.filter((unshieldEvent) => unshieldEvent.railgunTxid === railgunTransaction.hash);
            const blindedCommitmentsOutUnshieldEvents = (0, is_defined_1.removeUndefineds)(unshieldEventsForRailgunTxid.map((unshieldEvent) => unshieldEvent.blindedCommitment));
            if (unshieldEventsForRailgunTxid.length > 1) {
                throw new Error('Cannot have more than 1 unshield event per railgun txid');
            }
            if (!sentCommitmentsForRailgunTxid.length && !unshieldEventsForRailgunTxid.length) {
                throw new Error(`No sent commitments or unshield events for railgun txid: ${railgunTxid}`);
            }
            // Aggregate Sent + Unshields
            const blindedCommitmentsOut = (0, is_defined_1.removeDuplicates)([
                ...blindedCommitmentsOutSentCommitments,
                ...blindedCommitmentsOutUnshieldEvents,
            ]);
            if (blindedCommitmentsOut.length !== railgunTransaction.commitments.length) {
                throw new Error(`Not enough blinded commitments for railgun txid commitments: expected ${railgunTransaction.commitments.length}, got ${blindedCommitmentsOut.length}`);
            }
            const npksOut = [
                ...sentCommitmentsForRailgunTxid.map((sentCommitment) => sentCommitment.note.notePublicKey),
                ...unshieldEventsForRailgunTxid.map((unshieldEvent) => (0, bytes_1.hexToBigInt)(unshieldEvent.toAddress)),
            ];
            if (npksOut.length !== railgunTransaction.commitments.length) {
                throw new Error(`Invalid number of npksOut for railgun txid commitments: expected ${railgunTransaction.commitments.length}, got ${npksOut.length}`);
            }
            const valuesOut = [
                ...sentCommitmentsForRailgunTxid.map((sentCommitment) => sentCommitment.note.value),
                ...unshieldEventsForRailgunTxid.map((unshieldEvent) => BigInt(unshieldEvent.amount) + BigInt(unshieldEvent.fee)),
            ];
            if (valuesOut.length !== railgunTransaction.commitments.length) {
                throw new Error(`Invalid number of valuesOut for railgun txid commitments: expected ${railgunTransaction.commitments.length}, got ${valuesOut.length}`);
            }
            const poisPerList = sentCommitmentsForRailgunTxid.length
                ? sentCommitmentsForRailgunTxid[0].poisPerList
                : unshieldEventsForRailgunTxid[0].poisPerList;
            const poiProofInputs = {
                // --- Public inputs ---
                anyRailgunTxidMerklerootAfterTransaction: railgunTxidMerkletreeData.currentMerkleProofForTree.root,
                // --- Private inputs ---
                // Railgun Transaction info
                boundParamsHash: railgunTransaction.boundParamsHash,
                nullifiers: railgunTransaction.nullifiers,
                commitmentsOut: railgunTransaction.commitments,
                // Spender wallet info
                spendingPublicKey: this.spendingPublicKey,
                nullifyingKey: this.nullifyingKey,
                // Nullified notes data
                token: spentTXOs[0].note.tokenHash,
                randomsIn: spentTXOs.map((txo) => txo.note.random),
                valuesIn: spentTXOs.map((txo) => txo.note.value),
                utxoPositionsIn: spentTXOs.map((txo) => txo.position),
                utxoTreesIn: spentTXOs[0].tree,
                blindedCommitmentsIn,
                creationTxidsIn,
                // Commitment notes data
                npksOut,
                valuesOut,
                // Railgun txid tree
                railgunTxidMerkleProofIndices: railgunTxidMerkletreeData.currentMerkleProofForTree.indices,
                railgunTxidMerkleProofPathElements: railgunTxidMerkletreeData.currentMerkleProofForTree.elements,
            };
            await poi_1.POI.generateAndSubmitPOIAllLists(chain, poisPerList, railgunTxid, poiProofInputs, blindedCommitmentsOut, railgunTxidMerkletreeData.currentTxidIndexForTree, railgunTransaction.blockNumber, () => { });
        }
        catch (err) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
            throw new Error(`Error generating POI - txid ${railgunTxid}: ${err.message}`);
        }
    }
    static getCreationTxidsIn(txos) {
        const creationTxidsIn = txos.map((txo) => {
            if ((0, commitment_1.isSentCommitmentType)(txo.commitmentType)) {
                if (!(0, is_defined_1.isDefined)(txo.railgunTxid)) {
                    throw new Error(`No railgunTxid for TXO with tree/position ${txo.tree}:${txo.position}`);
                }
                return txo.railgunTxid;
            }
            return (0, shield_railgun_txid_1.getShieldRailgunTxid)(txo.tree, txo.position);
        });
        return creationTxidsIn;
    }
    async updateReceiveCommitmentCreatedPOIs(chain, tree, position, poisPerList) {
        const dbPath = this.getWalletReceiveCommitmentDBPrefix(chain, tree, position);
        const data = (await this.db.get(dbPath));
        const receiveCommitment = msgpack_lite_1.default.decode((0, bytes_1.arrayify)(data));
        receiveCommitment.poisPerList = poisPerList;
        await this.updateReceiveCommitmentInDB(chain, tree, position, receiveCommitment);
    }
    async updateSentCommitmentSpentPOIs(chain, tree, position, poisPerList) {
        const dbPath = this.getWalletSentCommitmentDBPrefix(chain, tree, position);
        const data = (await this.db.get(dbPath));
        const sentCommitment = msgpack_lite_1.default.decode((0, bytes_1.arrayify)(data));
        sentCommitment.poisPerList = poisPerList;
        await this.updateSentCommitmentInDB(chain, tree, position, sentCommitment);
    }
    static getPossibleChangeTokenAmounts(historyItem) {
        switch (historyItem.version) {
            case wallet_types_1.TransactionHistoryItemVersion.Unknown:
            case wallet_types_1.TransactionHistoryItemVersion.Legacy:
                // Legacy versions don't have change token amounts.
                return historyItem.transferTokenAmounts;
            case wallet_types_1.TransactionHistoryItemVersion.UpdatedAug2022:
            case wallet_types_1.TransactionHistoryItemVersion.UpdatedNov2022:
                return historyItem.changeTokenAmounts;
        }
        throw new Error('Unrecognized history item version');
    }
    /**
     * Gets transactions history
     * @param chain - chain type/id to get transaction history for
     * @returns history
     */
    async getTransactionHistory(chain, startingBlock) {
        const TXOs = await this.TXOs(chain);
        const filteredTXOs = AbstractWallet.filterTXOsByBlockNumber(TXOs, startingBlock);
        const [receiveHistory, spendHistory] = await Promise.all([
            AbstractWallet.getTransactionReceiveHistory(filteredTXOs),
            this.getTransactionSpendHistory(chain, filteredTXOs, startingBlock),
        ]);
        const history = spendHistory.map((sendItem) => ({
            ...sendItem,
            receiveTokenAmounts: [],
        }));
        // Merge "spent" history with "receive" history items.
        // We have to remove all "receive" items that are change outputs.
        receiveHistory.forEach((receiveItem) => {
            let alreadyExistsInHistory = false;
            history.forEach((existingHistoryItem) => {
                if (receiveItem.txid === existingHistoryItem.txid) {
                    alreadyExistsInHistory = true;
                    const changeTokenAmounts = AbstractWallet.getPossibleChangeTokenAmounts(existingHistoryItem);
                    receiveItem.receiveTokenAmounts.forEach((receiveTokenAmount) => {
                        const matchingChangeOutput = changeTokenAmounts.find((ta) => ta.tokenHash === receiveTokenAmount.tokenHash &&
                            ta.amount === receiveTokenAmount.amount);
                        if (matchingChangeOutput &&
                            [
                                wallet_types_1.TransactionHistoryItemVersion.Unknown,
                                wallet_types_1.TransactionHistoryItemVersion.Legacy,
                            ].includes(existingHistoryItem.version)) {
                            // Remove change output (stored in transferTokenAmounts in legacy history items)
                            // Move to change outputs
                            const index = existingHistoryItem.transferTokenAmounts.findIndex((ta) => ta.tokenHash === receiveTokenAmount.tokenHash &&
                                ta.amount === receiveTokenAmount.amount);
                            existingHistoryItem.transferTokenAmounts.splice(index, 1);
                            existingHistoryItem.changeTokenAmounts.push(matchingChangeOutput);
                        }
                        else if (!matchingChangeOutput) {
                            // Receive token amount is not a "change" output.
                            // Add it to the history item.
                            existingHistoryItem.receiveTokenAmounts.push(receiveTokenAmount);
                        }
                    });
                }
            });
            if (!alreadyExistsInHistory) {
                history.unshift({
                    ...receiveItem,
                    transferTokenAmounts: [],
                    changeTokenAmounts: [],
                    unshieldTokenAmounts: [],
                    version: wallet_types_1.TransactionHistoryItemVersion.Unknown,
                });
            }
        });
        return history;
    }
    static filterTXOsByBlockNumber(txos, startingBlock) {
        if (startingBlock == null) {
            return txos;
        }
        let hasShownNoBlockNumbersError = false;
        return txos.filter((txo) => {
            if (!(0, is_defined_1.isDefined)(txo.note.blockNumber)) {
                if (!hasShownNoBlockNumbersError) {
                    // This will occur for legacy scanned notes.
                    // New notes will have block number, and will optimize the history response.
                    debugger_1.default.error(new Error('No blockNumbers for TXOs'));
                    hasShownNoBlockNumbersError = true;
                }
                return true;
            }
            return txo.note.blockNumber >= startingBlock;
        });
    }
    /**
     * Gets transactions history for "received" transactions
     * @param chain - chain type/id to get balances for
     * @returns history
     */
    static getTransactionReceiveHistory(filteredTXOs) {
        const txidTransactionMap = {};
        filteredTXOs.forEach(({ txid, timestamp, note }) => {
            if (note.value === 0n) {
                return;
            }
            if (!(0, is_defined_1.isDefined)(txidTransactionMap[txid])) {
                txidTransactionMap[txid] = {
                    txid,
                    timestamp,
                    blockNumber: note.blockNumber,
                    receiveTokenAmounts: [],
                };
            }
            const tokenHash = (0, bytes_1.formatToByteLength)(note.tokenHash, bytes_1.ByteLength.UINT_256, false);
            txidTransactionMap[txid].receiveTokenAmounts.push({
                tokenHash,
                tokenData: note.tokenData,
                amount: note.value,
                memoText: note.memoText,
                senderAddress: note.getSenderAddress(),
                shieldFee: note.shieldFee,
            });
        });
        const history = Object.values(txidTransactionMap);
        return history;
    }
    static getTransactionHistoryItemVersion(noteAnnotationData, isLegacyTransactNote) {
        if (!noteAnnotationData) {
            return wallet_types_1.TransactionHistoryItemVersion.Legacy;
        }
        if (isLegacyTransactNote) {
            return wallet_types_1.TransactionHistoryItemVersion.UpdatedAug2022;
        }
        return wallet_types_1.TransactionHistoryItemVersion.UpdatedNov2022;
    }
    async getAllUnshieldEventsFromSpentNullifiers(chain, filteredTXOs) {
        const merkletree = this.getUTXOMerkletreeForChain(chain);
        const unshieldEvents = [];
        const seenSpentTxids = [];
        const spentTXOs = filteredTXOs.filter((txo) => (0, is_defined_1.isDefined)(txo.spendtxid) && txo.spendtxid !== false);
        await Promise.all(spentTXOs.map(async ({ spendtxid, note }) => {
            if (note.value === 0n) {
                return;
            }
            if (spendtxid === false || !spendtxid) {
                return;
            }
            if (seenSpentTxids.includes(spendtxid)) {
                return;
            }
            seenSpentTxids.push(spendtxid);
            // Nullifier exists. Find unshield events from txid.
            const unshieldEventsForNullifier = await merkletree.getUnshieldEvents(spendtxid);
            const filteredUnshieldEventsForNullifier = unshieldEventsForNullifier.filter((event) => unshieldEvents.find((existingUnshieldEvent) => AbstractWallet.compareUnshieldEvents(existingUnshieldEvent, event)) == null);
            await Promise.all(filteredUnshieldEventsForNullifier.map(async (filteredUnshieldEvent) => {
                const unshieldEvent = filteredUnshieldEvent;
                // Add railgunTxid and blindedCommitment, if they don't already exist.
                if (!(0, is_defined_1.isDefined)(unshieldEvent.railgunTxid) ||
                    !(0, is_defined_1.isDefined)(unshieldEvent.blindedCommitment)) {
                    const commitmentHash = (0, bytes_1.nToHex)((0, note_util_1.getUnshieldEventNoteHash)(unshieldEvent), bytes_1.ByteLength.UINT_256, true);
                    const txidMerkletree = this.getRailgunTXIDMerkletreeForChain(chain);
                    const commitmentMap = await txidMerkletree.getRailgunTxidsForCommitments([
                        commitmentHash,
                    ]);
                    const railgunTxid = commitmentMap[commitmentHash];
                    if ((0, is_defined_1.isDefined)(railgunTxid)) {
                        unshieldEvent.railgunTxid = railgunTxid;
                        unshieldEvent.blindedCommitment = (0, blinded_commitment_1.getBlindedCommitmentForUnshield)(commitmentHash, unshieldEvent.toAddress, railgunTxid);
                        await merkletree.updateUnshieldEvent(unshieldEvent);
                    }
                }
                unshieldEvents.push(unshieldEvent);
            }));
        }));
        return unshieldEvents;
    }
    static compareUnshieldEvents(a, b) {
        return a.txid === b.txid && a.eventLogIndex === b.eventLogIndex;
    }
    async getTransactionSpendHistory(chain, filteredTXOs, startingBlock) {
        const [allUnshieldEvents, sentCommitments] = await Promise.all([
            this.getAllUnshieldEventsFromSpentNullifiers(chain, filteredTXOs),
            this.getSentCommitments(chain, startingBlock),
        ]);
        const txidTransactionMap = {};
        sentCommitments.forEach(({ txid, timestamp, note, isLegacyTransactNote, noteAnnotationData }) => {
            if (note.value === 0n) {
                return;
            }
            if (!(0, is_defined_1.isDefined)(txidTransactionMap[txid])) {
                txidTransactionMap[txid] = {
                    txid,
                    timestamp,
                    blockNumber: note.blockNumber,
                    tokenAmounts: [],
                    unshieldEvents: [],
                    version: AbstractWallet.getTransactionHistoryItemVersion(noteAnnotationData, isLegacyTransactNote),
                };
            }
            const tokenHash = (0, bytes_1.formatToByteLength)(note.tokenHash, bytes_1.ByteLength.UINT_256, false);
            const tokenAmount = {
                tokenHash,
                tokenData: note.tokenData,
                amount: note.value,
                noteAnnotationData,
                memoText: note.memoText,
            };
            const isNonLegacyTransfer = !isLegacyTransactNote &&
                (0, is_defined_1.isDefined)(noteAnnotationData) &&
                noteAnnotationData.outputType === formatted_types_1.OutputType.Transfer;
            if (isNonLegacyTransfer) {
                tokenAmount.recipientAddress = (0, bech32_1.encodeAddress)(note.receiverAddressData);
            }
            txidTransactionMap[txid].tokenAmounts.push(tokenAmount);
        });
        // Add unshield events to txidTransactionMap
        allUnshieldEvents.forEach((unshieldEvent) => {
            const foundUnshieldTransactionInTransactCommitments = txidTransactionMap[unshieldEvent.txid];
            if (!(0, is_defined_1.isDefined)(foundUnshieldTransactionInTransactCommitments)) {
                // This will occur on a self-signed unshield.
                // There is no commitment (or tokenAmounts) for this kind of unshield transaction.
                txidTransactionMap[unshieldEvent.txid] = {
                    txid: unshieldEvent.txid,
                    timestamp: unshieldEvent.timestamp,
                    blockNumber: unshieldEvent.blockNumber,
                    unshieldEvents: [unshieldEvent],
                    tokenAmounts: [],
                    version: wallet_types_1.TransactionHistoryItemVersion.UpdatedNov2022,
                };
                return;
            }
            // Unshield event exists. Add to its amount rather than creating a new event.
            // Multiple unshields of the same token can occur in cases of complex circuits. (More than 10 inputs to the unshield).
            const existingUnshieldEvent = txidTransactionMap[unshieldEvent.txid].unshieldEvents.find((existingEvent) => AbstractWallet.compareUnshieldEvents(existingEvent, unshieldEvent));
            if (existingUnshieldEvent) {
                // Add amount to existing unshield event.
                existingUnshieldEvent.amount = (BigInt(existingUnshieldEvent.amount) + BigInt(unshieldEvent.amount)).toString();
                return;
            }
            txidTransactionMap[unshieldEvent.txid].unshieldEvents.push(unshieldEvent);
        });
        const preProcessHistory = Object.values(txidTransactionMap);
        const history = preProcessHistory.map(({ txid, timestamp, blockNumber, tokenAmounts, unshieldEvents, version }) => {
            const transferTokenAmounts = [];
            let relayerFeeTokenAmount;
            const changeTokenAmounts = [];
            tokenAmounts.forEach((tokenAmount) => {
                if (!tokenAmount.noteAnnotationData) {
                    // Legacy notes without extra data, consider as a simple "transfer".
                    transferTokenAmounts.push(tokenAmount);
                    return;
                }
                switch (tokenAmount.noteAnnotationData.outputType) {
                    case formatted_types_1.OutputType.Transfer:
                        transferTokenAmounts.push(tokenAmount);
                        break;
                    case formatted_types_1.OutputType.RelayerFee:
                        relayerFeeTokenAmount = tokenAmount;
                        break;
                    case formatted_types_1.OutputType.Change:
                        changeTokenAmounts.push(tokenAmount);
                        break;
                }
            });
            const unshieldTokenAmounts = unshieldEvents.map((unshieldEvent) => {
                const tokenData = (0, note_util_1.serializeTokenData)(unshieldEvent.tokenAddress, unshieldEvent.tokenType, unshieldEvent.tokenSubID);
                const tokenHash = (0, note_util_1.getTokenDataHash)(tokenData);
                return {
                    tokenHash,
                    tokenData,
                    amount: BigInt(unshieldEvent.amount),
                    memoText: undefined,
                    recipientAddress: unshieldEvent.toAddress,
                    senderAddress: undefined,
                    unshieldFee: unshieldEvent.fee,
                };
            });
            const historyEntry = {
                txid,
                timestamp,
                blockNumber,
                transferTokenAmounts,
                relayerFeeTokenAmount,
                changeTokenAmounts,
                unshieldTokenAmounts,
                version,
            };
            return historyEntry;
        });
        return history;
    }
    getUTXOMerkletreeForChain(chain) {
        const merkletree = this.utxoMerkletrees[chain.type][chain.id];
        if (!(0, is_defined_1.isDefined)(merkletree)) {
            throw new Error(`No utxo merkletree for chain ${chain.type}:${chain.id}`);
        }
        return merkletree;
    }
    getRailgunTXIDMerkletreeForChain(chain) {
        const merkletree = this.railgunTxidMerkletrees[chain.type][chain.id];
        if (!(0, is_defined_1.isDefined)(merkletree)) {
            throw new Error(`No txid merkletree for chain ${chain.type}:${chain.id}`);
        }
        return merkletree;
    }
    /**
     * Gets wallet balances
     * @param chain - chain type/id to get balances for
     * @returns balances
     */
    async balances(chain) {
        const TXOs = await this.TXOs(chain);
        const balances = {};
        // Loop through each TXO and add to balances if unspent
        TXOs.forEach((txo) => {
            const tokenHash = (0, bytes_1.formatToByteLength)(txo.note.tokenHash, bytes_1.ByteLength.UINT_256, false);
            // If we don't have an entry for this token yet, create one
            if (!(0, is_defined_1.isDefined)(balances[tokenHash])) {
                balances[tokenHash] = {
                    balance: BigInt(0),
                    utxos: [],
                    tokenData: txo.note.tokenData,
                };
            }
            // If utxo is unspent process it
            if (txo.spendtxid === false) {
                // Store utxo
                balances[tokenHash].utxos.push(txo);
                // Increment balance
                balances[tokenHash].balance += txo.note.value;
            }
        });
        return balances;
    }
    async getBalance(chain, tokenAddress) {
        const balances = await this.balances(chain);
        const balanceForToken = balances[(0, bytes_1.formatToByteLength)(tokenAddress, 32, false)];
        return (0, is_defined_1.isDefined)(balanceForToken) ? balanceForToken.balance : undefined;
    }
    /**
     * Sort token balances by tree
     * @param chain - chain type/id of token
     * @returns balances by tree
     */
    async balancesByTree(chain) {
        // Fetch balances
        const balances = await this.balances(chain);
        // Sort token balances by tree
        const balancesByTree = {};
        // Loop through each token
        Object.keys(balances).forEach((token) => {
            // Create balances tree array
            balancesByTree[token] = [];
            // Loop through each TXO and sort by tree
            balances[token].utxos.forEach((utxo) => {
                if (!(0, is_defined_1.isDefined)(balancesByTree[token][utxo.tree])) {
                    balancesByTree[token][utxo.tree] = {
                        balance: utxo.note.value,
                        utxos: [utxo],
                        tokenData: utxo.note.tokenData,
                    };
                }
                else {
                    balancesByTree[token][utxo.tree].balance += utxo.note.value;
                    balancesByTree[token][utxo.tree].utxos.push(utxo);
                }
            });
        });
        return balancesByTree;
    }
    async balancesByTreeForToken(chain, tokenHash) {
        const balances = await this.balancesByTree(chain);
        const treeSortedBalances = balances[tokenHash] ?? [];
        return treeSortedBalances;
    }
    static tokenBalanceAcrossAllTrees(treeSortedBalances) {
        const tokenBalance = treeSortedBalances.reduce((left, right) => left + right.balance, BigInt(0));
        return tokenBalance;
    }
    /**
     * Scans for new balances
     * @param chain - chain data to scan
     */
    async scanBalances(chain, progressCallback) {
        debugger_1.default.log(`scan wallet balances: chain ${chain.type}:${chain.id}`);
        const utxoMerkletree = this.getUTXOMerkletreeForChain(chain);
        try {
            // Fetch wallet details and latest tree.
            const [walletDetails, latestTree] = await Promise.all([
                this.getWalletDetails(chain),
                utxoMerkletree.latestTree(),
            ]);
            // Fill list of tree heights with 0s up to # of trees
            while (walletDetails.treeScannedHeights.length <= latestTree) {
                walletDetails.treeScannedHeights.push(0);
            }
            if (this.creationBlockNumbers &&
                this.creationBlockNumbers[chain.type] != null &&
                this.creationBlockNumbers[chain.type][chain.id] != null &&
                (walletDetails.creationTree == null || walletDetails.creationTreeHeight == null)) {
                const creationBlockNumber = this.creationBlockNumbers[chain.type][chain.id];
                const creationTreeInfo = await AbstractWallet.getTreeAndPositionBeforeBlock(utxoMerkletree, latestTree, creationBlockNumber);
                if (creationTreeInfo != null) {
                    walletDetails.creationTree = creationTreeInfo.tree;
                    walletDetails.creationTreeHeight = creationTreeInfo.position;
                }
            }
            const startScanTree = walletDetails.creationTree ?? 0;
            const treesToScan = latestTree - startScanTree + 1;
            // Loop through each tree and scan
            for (let treeIndex = startScanTree; treeIndex <= latestTree; treeIndex += 1) {
                // Get scanned height
                let startScanHeight = walletDetails.treeScannedHeights[treeIndex];
                // If creationTreeHeight exists, check if it is higher than default startScanHeight and start there if needed
                if (treeIndex === walletDetails.creationTree &&
                    (0, is_defined_1.isDefined)(walletDetails.creationTreeHeight)) {
                    startScanHeight = Math.max(walletDetails.creationTreeHeight, startScanHeight);
                }
                // Create sparse array of tree
                const treeHeight = await utxoMerkletree.getTreeLength(treeIndex);
                const fetcher = new Array(treeHeight);
                // Fetch each leaf we need to scan
                for (let index = startScanHeight; index < treeHeight; index += 1) {
                    fetcher[index] = utxoMerkletree.getCommitment(treeIndex, index);
                }
                // Wait until all leaves are fetched
                const leaves = await Promise.all(fetcher);
                const leavesToScan = treeHeight - startScanHeight;
                let finishedLeafCount = 0;
                let timeSinceLastProgressCallback = Date.now() - 500;
                // Start scanning primary and change
                await this.scanLeaves(leaves, treeIndex, chain, startScanHeight, treeHeight, () => {
                    // Scan ticker. Triggers every time leaf is scanned successfully or skipped.
                    if (progressCallback) {
                        // Throttle progressCallback, at most every 500ms.
                        if (Date.now() - timeSinceLastProgressCallback >= 500) {
                            // 100ms since last progressCallback call.
                            timeSinceLastProgressCallback = Date.now();
                            const finishedTreeCount = treeIndex - startScanTree;
                            const finishedTreesProgress = finishedTreeCount / treesToScan;
                            finishedLeafCount += 1;
                            const newTreeProgress = finishedLeafCount / leavesToScan / treesToScan;
                            progressCallback(finishedTreesProgress + newTreeProgress);
                        }
                    }
                });
                // Commit new scanned height
                walletDetails.treeScannedHeights[treeIndex] = leaves.length;
                // Write new wallet details to db
                await this.db.put(this.getWalletDetailsPath(chain), msgpack_lite_1.default.encode(walletDetails));
            }
            // Emit scanned event for this chain
            debugger_1.default.log(`wallet: scanned ${chain.type}:${chain.id}`);
            this.emit(event_types_1.EngineEvent.WalletScanComplete, { chain });
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.refreshPOIs(chain); // Synchronous
        }
        catch (err) {
            if (err instanceof Error) {
                debugger_1.default.log(`wallet.scan error: ${err.message}`);
                debugger_1.default.error(err);
            }
        }
    }
    /**
     * Occurs after balances are scanned.
     */
    async refreshPOIs(chain) {
        var _a, _b;
        if (!poi_1.POI.isActiveForChain(chain)) {
            return;
        }
        if (this.isRefreshingPOIs[chain.type]?.[chain.id]) {
            return;
        }
        (_a = this.isRefreshingPOIs)[_b = chain.type] ?? (_a[_b] = []);
        this.isRefreshingPOIs[chain.type][chain.id] = true;
        try {
            // Refresh POIs - Receive commitments
            await this.refreshReceivePOIsAllTXOs(chain);
            // Refresh POIs - Sent commitments / unshields
            await this.refreshSpentPOIsAllSentCommitmentsAndUnshieldEvents(chain);
            // Generate POIs - Sent commitments / unshields
            // TODO - add this after manual testing
            // await this.generatePOIsAllSentCommitmentsAndUnshieldEvents(chain);
            this.isRefreshingPOIs[chain.type][chain.id] = false;
        }
        catch (err) {
            debugger_1.default.error(err);
            this.isRefreshingPOIs[chain.type][chain.id] = false;
            throw err;
        }
    }
    /**
     * Searches for creation tree height for given merkletree.
     * @param merkletree - Merkletree
     * @param latestTree - number
     */
    static async getTreeAndPositionBeforeBlock(utxoMerkletree, latestTree, creationBlockNumber) {
        if (creationBlockNumber == null) {
            return undefined;
        }
        // Loop through each tree, descending, and search commitments for creation tree height <= block number
        for (let tree = latestTree; tree > -1; tree -= 1) {
            const treeHeight = await utxoMerkletree.getTreeLength(tree);
            const fetcher = new Array(treeHeight);
            // Build reverse list.
            for (let index = 0; index < treeHeight; index += 1) {
                fetcher[index] = utxoMerkletree.getCommitment(tree, index);
            }
            const leaves = await Promise.all(fetcher);
            if (!leaves.length) {
                return undefined;
            }
            // Search through leaves (descending) for first blockNumber before creation.
            const creationBlockIndex = (0, search_1.binarySearchForUpperBoundIndex)(leaves, (commitment) => commitment != null &&
                commitment.blockNumber != null &&
                commitment.blockNumber <= creationBlockNumber);
            if (creationBlockIndex > -1) {
                return { tree, position: creationBlockIndex };
            }
        }
        return undefined;
    }
    setCreationBlockNumbers(creationBlockNumbers) {
        this.creationBlockNumbers = creationBlockNumbers;
    }
    /**
     * Clears balances scanned from merkletrees and stored to database.
     * @param chain - chain type/id to clear
     */
    async clearScannedBalances(chain) {
        const walletDetails = await this.getWalletDetails(chain);
        this.cachedReceiveCommitments = [];
        // Clear namespace and then resave the walletDetails
        const namespace = this.getWalletDetailsPath(chain);
        await this.db.clearNamespace(namespace);
        walletDetails.treeScannedHeights = [];
        await this.db.put(this.getWalletDetailsPath(chain), msgpack_lite_1.default.encode(walletDetails));
    }
    /**
     * Clears stored balances and re-scans fully.
     * @param chain - chain type/id to rescan
     */
    async fullRescanBalances(chain, progressCallback) {
        await this.clearScannedBalances(chain);
        return this.scanBalances(chain, progressCallback);
    }
    static dbPath(id) {
        return [(0, bytes_1.fromUTF8String)('wallet'), id];
    }
    static async read(db, id, encryptionKey) {
        return msgpack_lite_1.default.decode((0, bytes_1.arrayify)(await db.getEncrypted(AbstractWallet.dbPath(id), encryptionKey)));
    }
    static async write(db, id, encryptionKey, data) {
        await db.putEncrypted(AbstractWallet.dbPath(id), encryptionKey, msgpack_lite_1.default.encode(data));
    }
    static async delete(db, id) {
        return db.del(AbstractWallet.dbPath(id));
    }
    /**
     * Loads encrypted wallet data from database.
     * @param db - database
     * @param encryptionKey - encryption key to use with database
     * @param id - wallet id
     */
    static async getEncryptedData(db, encryptionKey, id) {
        return msgpack_lite_1.default.decode((0, bytes_1.arrayify)(await db.getEncrypted([(0, bytes_1.fromUTF8String)('wallet'), id], encryptionKey)));
    }
    static getKeysFromShareableViewingKey(shareableViewingKey) {
        try {
            const { vpriv: viewingPrivateKey, spub: spendingPublicKeyString } = msgpack_lite_1.default.decode(Buffer.from(shareableViewingKey, 'hex'));
            const spendingPublicKey = (0, babyjubjub_1.unpackPoint)(Buffer.from(spendingPublicKeyString, 'hex'));
            return { viewingPrivateKey, spendingPublicKey };
        }
        catch (err) {
            throw new Error('Invalid shareable private key.');
        }
    }
    generateShareableViewingKey() {
        const spendingPublicKeyString = (0, babyjubjub_1.packPoint)(this.spendingPublicKey).toString('hex');
        const data = {
            vpriv: (0, bytes_1.formatToByteLength)(this.viewingKeyPair.privateKey, bytes_1.ByteLength.UINT_256),
            spub: spendingPublicKeyString,
        };
        return msgpack_lite_1.default.encode(data).toString('hex');
    }
}
exports.AbstractWallet = AbstractWallet;
